import { useEffect } from 'react';
import { Navigate, NavLink, Outlet, useLocation } from 'react-router';
import { Icon, type IconName } from '../components/Icon.js';
import { Logo } from '../components/Logo.js';
import { useAdminAuthStore } from './adminAuthStore.js';

/** `operator`: also shown to operators. The server enforces the same split. */
const NAV: { to: string; label: string; icon: IconName; end?: boolean; operator?: boolean }[] = [
  { to: '/admin', label: 'Overview', icon: 'barChart', end: true, operator: true },
  { to: '/admin/users', label: 'Users', icon: 'users', operator: true },
  { to: '/admin/channels', label: 'Channels', icon: 'messageSquare' },
  { to: '/admin/directory', label: 'Directory', icon: 'server' },
  { to: '/admin/settings', label: 'Settings', icon: 'settings' },
];

/** Gate for `/admin/*`: an administrator session, never a user one. */
export function RequireAdmin() {
  const status = useAdminAuthStore((state) => state.status);
  const bootstrap = useAdminAuthStore((state) => state.bootstrap);
  const location = useLocation();

  useEffect(() => {
    if (status === 'loading') {
      void bootstrap();
    }
  }, [status, bootstrap]);

  if (status === 'loading') {
    return <div className="centered muted">Checking the administrator session…</div>;
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  return <Outlet />;
}

/** Sections only a full administrator may open; an operator is sent to Users. */
export function RequireFullAdmin() {
  const role = useAdminAuthStore((state) => state.role);
  return role === 'full' ? <Outlet /> : <Navigate to="/admin/users" replace />;
}

/** The admin shell: a rail of sections and the current page. */
export function AdminLayout() {
  const username = useAdminAuthStore((state) => state.username);
  const role = useAdminAuthStore((state) => state.role);
  const logout = useAdminAuthStore((state) => state.logout);

  return (
    <div className="admin-shell">
      <aside className="admin-rail">
        <div className="admin-brand">
          <Logo size={24} />
          <span className="admin-badge">
            <Icon name="shieldCheck" size={12} />
            Admin
          </span>
        </div>

        <nav className="admin-nav" aria-label="Administration">
          {NAV.filter((item) => role === 'full' || item.operator).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end ?? false}
              className={({ isActive }) =>
                isActive ? 'admin-link admin-link--active' : 'admin-link'
              }
            >
              <Icon name={item.icon} size={16} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="admin-rail-foot">
          <span className="admin-who truncate">
            <Icon name="lock" size={13} />
            {username}
            {role === 'operator' ? ' (operator)' : ''}
          </span>
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => void logout()}>
            <Icon name="logOut" size={13} />
            Sign out
          </button>
        </div>
      </aside>

      <main className="admin-main">
        <Outlet />
      </main>
    </div>
  );
}
